import classNames from "classnames";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DashedText } from "../elements/DashedText";
import { RTF } from "../elements/RTF";
import { Separator } from "../elements/Separator";
import { SectionPaddingTopClassName } from "../styles";
import { CollectionProps } from "./Collection";

interface FormInformation {
  message: string;
  name: string;
  email: string;
  phone: string;
  enquiry: string;
  enquiryPlaceholder: string;
  submit: string;
  submitting: string;
}

export const TeamCTA: React.FC<CollectionProps> = ({
  message,
  contents,
  className,
  shape,
  title,
  contactForm,
}) => {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [enquiry, setEnquiry] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const formInfo =
    contactForm?.controlInformation as unknown as FormInformation;
  const showSubmissionMessage = () => {
    setHasSubmitted(true);
    const timer = setTimeout(() => {
      setHasSubmitted(false);
    }, 7000);
    return () => clearTimeout(timer);
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    const response = await fetch(
      "https://getform.io/f/82657f8b-9b37-4fcd-b6f9-15a3b027ff28",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: fullName,
          phoneNumber: phoneNumber,
          email: email,
          enquiry: enquiry,
        }),
      }
    );
    if (!response.ok) {
      const errorMessage = await response.text();
      setIsSubmitting(false);
      setHasSubmitted(false);
      console.error(errorMessage);
      return null;
    }
    setIsSubmitting(false);
    showSubmissionMessage();
    setFullName("");
    setEmail("");
    setPhoneNumber("");
    setEnquiry("");
    return response.json();
  }

  return (
    <section
      data-component="Team CTA"
      className={classNames(
        isSubmitting && "cursor-wait",
        className,
        "relative",
        SectionPaddingTopClassName
      )}
    >
      <div className="max-width relative mx-20px grid gap-x-mobile md:grid-cols-tablet md:gap-x-tablet lg:grid-cols-desktop lg:gap-x-desktop">
        {shape && (
          <div className="col-span-1 col-start-1 row-span-3 row-start-1 md:col-span-4 md:col-start-2 lg:col-span-6 lg:col-start-3">
            <img
              src={shape.encoded ?? shape.encodedUrl}
              className="h-full-laptop object-contain"
              alt=""
            />
          </div>
        )}
        <div
          className={classNames(
            "col-span-2 col-start-1 md:col-span-5 md:col-start-5 lg:col-span-6 lg:col-start-3",
            "relative row-span-1 row-start-2 mb-auto mt-40px pr-10 pt-10px"
          )}
        >
          {title && <h3 className={classNames("h1")}>{title}</h3>}
          {message && (
            <RTF
              content={message}
              fixedParagraphClassName="p2"
              className="mt-20px"
            />
          )}
        </div>
        {hasSubmitted && (
          <h3 className="h3 col-span-2 col-start-1 row-start-4 my-40px text-light-green lg:col-span-6 lg:col-start-9 lg:row-span-2 lg:row-start-2">
            {formInfo.message}
          </h3>
        )}
        {!hasSubmitted && (
          <form
            id="contactForm"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={(e) => handleSubmit(e)}
            action="https://getform.io/f/82657f8b-9b37-4fcd-b6f9-15a3b027ff28"
            method="POST"
            className={classNames(
              className,
              "contactForm relative col-span-2 col-start-1 row-start-4 my-40px grid lg:col-span-6 lg:col-start-9 lg:row-span-2 lg:row-start-2"
            )}
          >
            <label htmlFor="fullName">{formInfo.name}</label>
            <input
              name="fullName"
              type="text"
              placeholder={t("yourName") || "??"}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required={true}
            />
            <label htmlFor="email">{formInfo.email}</label>
            <input
              name="Email"
              type="text"
              placeholder="example@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required={true}
            />
            <label htmlFor="phoneNumber">{formInfo.phone}</label>
            <input
              name="phoneNumber"
              type="tel"
              placeholder="00 000 00 00"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            <label htmlFor="enquiry">{formInfo.enquiry}</label>
            <textarea
              name="enquiry"
              placeholder={formInfo.enquiryPlaceholder}
              value={enquiry}
              onChange={(e) => setEnquiry(e.target.value)}
            />
            <button
              aria-label="submit"
              type="submit"
              form="contactForm"
              className="submitButton"
            >
              <DashedText>{formInfo.submit}</DashedText>
            </button>
            <div>
              <p
                className={classNames(
                  isSubmitting ? "text-light-green" : "text-transparent",
                  "p3 my-20px"
                )}
              >
                {formInfo.submitting}
              </p>
            </div>
          </form>
        )}
      </div>
      <Separator
        className="col-span-full col-start-1"
        spaceClass="mt-30px lg:mt-60px"
      />
      <div
        className={classNames(
          "max-width",
          "relative grid grid-cols-1 md:grid-cols-2"
        )}
      >
        {contents.map(({ content }) => content)}
      </div>
    </section>
  );
};
